// M14 — decor kind catalogue. Pure metadata, no runtime registry. The
// authoritative sprite-path mapping lives client-side in
// client/src/render/decor.ts (DECOR_SPRITES); this list is the source
// of truth for *which kinds exist*, consumed by:
//   - admin zones-screen.ts (dropdown of placeable kinds)
//   - client decor.ts (asserts every kind has a sprite path at boot)
//
// Categories are loosely grouped for the admin UI (let dropdowns
// section by family). To add a new kind: append to its category here,
// then add a matching entry in client DECOR_SPRITES with the asset
// path. The client warns once if a kind has no sprite (graceful
// degradation; the placement just renders nothing).

export const DECOR_TREES = [
  "oak_1",
  "oak_2",
  "oak_3",
  "oak_4",
] as const;

export const DECOR_SAND = [
  "sand_1",
] as const;

export const DECOR_ROCKS = [
  "rock_pebble_scatter",
  "rock_boulder",
  "rock_mossy",
  "rock_jagged",
  "rock_slab",
  "rock_pillar",
  "rock_cluster",
  "rock_reddish",
] as const;

export const DECOR_BUSHES = [
  "bush_small",
  "bush_large",
  "bush_flowered",
  "bush_dead",
] as const;

export const DECOR_PLANTS = [
  "grass_tuft",
  "grass_tall",
  "reeds",
  "flowers_red",
  "flowers_blue",
  "flowers_yellow",
] as const;

// Flat ordered list — the dropdown iterates this. Order matters for
// UX (most-used kinds first within each family).
export const DECOR_KINDS = [
  ...DECOR_TREES,
  ...DECOR_BUSHES,
  ...DECOR_ROCKS,
  ...DECOR_PLANTS,
  ...DECOR_SAND,
] as const;

export type DecorKind = (typeof DECOR_KINDS)[number];

// Category lookup for the admin UI (colored markers, dropdown grouping).
export type DecorCategory = "trees" | "bushes" | "rocks" | "plants" | "sand";

const CATEGORY_OF: Record<string, DecorCategory> = {};
for (const k of DECOR_TREES) CATEGORY_OF[k] = "trees";
for (const k of DECOR_BUSHES) CATEGORY_OF[k] = "bushes";
for (const k of DECOR_ROCKS) CATEGORY_OF[k] = "rocks";
for (const k of DECOR_PLANTS) CATEGORY_OF[k] = "plants";
for (const k of DECOR_SAND) CATEGORY_OF[k] = "sand";

export function decorCategoryOf(kind: string): DecorCategory | null {
  return CATEGORY_OF[kind] ?? null;
}

// Marker colors for the admin canvas (top-down dot + iso fallback when
// the actual sprite hasn't loaded yet). Same palette as the in-game
// terrain just shifted toward family identity.
export const DECOR_CATEGORY_COLOR: Record<DecorCategory, string> = {
  trees: "#4a8a3a",
  bushes: "#6dab4f",
  rocks: "#7a7a78",
  plants: "#9bc070",
  sand: "#d9c89a",
};

// Sprite asset paths. Served by the client dev server / static-asset
// host at /assets/decor/.... Consumed by both:
//   - client/src/render/decor.ts (PixiJS Sprite via Assets.load)
//   - admin/src/zones-screen.ts (Image() for iso canvas decor preview)
// Both apps treat a missing entry as "kind has no visual" — placement
// works but the sprite renders as a marker only. Add a path here when
// a new kind ships with an actual PNG.
export const DECOR_SPRITES: Record<string, string> = {
  oak_1: "/assets/decor/trees/oak.png",
  oak_2: "/assets/decor/trees/oak2.png",
  oak_3: "/assets/decor/trees/oak3.png",
  oak_4: "/assets/decor/trees/oak4.png",
  sand_1: "/assets/decor/sand/sand.png",
  rock_pebble_scatter: "/assets/decor/rocks/pebble_scatter.png",
  rock_boulder: "/assets/decor/rocks/boulder.png",
  rock_mossy: "/assets/decor/rocks/mossy.png",
  rock_jagged: "/assets/decor/rocks/jagged.png",
  rock_slab: "/assets/decor/rocks/slab.png",
  rock_pillar: "/assets/decor/rocks/pillar.png",
  rock_cluster: "/assets/decor/rocks/cluster.png",
  rock_reddish: "/assets/decor/rocks/reddish.png",
  bush_small: "/assets/decor/bushes/small.png",
  bush_large: "/assets/decor/bushes/large.png",
  bush_flowered: "/assets/decor/bushes/flowered.png",
  bush_dead: "/assets/decor/bushes/dead.png",
  grass_tuft: "/assets/decor/plants/grass_tuft.png",
  grass_tall: "/assets/decor/plants/grass_tall.png",
  reeds: "/assets/decor/plants/reeds.png",
  flowers_red: "/assets/decor/plants/flowers_red.png",
  flowers_blue: "/assets/decor/plants/flowers_blue.png",
  flowers_yellow: "/assets/decor/plants/flowers_yellow.png",
};
